/**
 * The base route table, as data and without components (those are in `./pages.js`), so
 * company folders and the access model can read it without importing every page.
 *
 *  - `key`: the page key. Companies replace a page (`pages`) or drop a route (`remove`) by it.
 *  - `path`: the URL.
 *  - `screen`: the nav key(s) the route belongs to. A company whose navigation leaves out
 *    every screen of a route does not mount it. `null`: public, always mounted.
 *  - `guard`: wrapped in RequireAccess with this nav key (the screen permission check).
 *
 * Order and guards are exactly the routes the admin had before the company layer.
 */
export const BASE_ROUTES = Object.freeze([
    { key: 'Landing', path: '/', screen: null },
    { key: 'Login', path: '/login', screen: null },
    { key: 'ForgotPassword', path: '/forgot-password', screen: null },
    { key: 'Support', path: '/support', screen: null },
    { key: 'PrivacyPolicy', path: '/privacy-policy', screen: null },
    { key: 'Dashboard', path: '/dashboard', screen: 'dashboard', guard: 'dashboard' },
    { key: 'Notifications', path: '/dashboard/notifications', screen: 'notifications', guard: 'notifications' },
    { key: 'Clients', path: '/dashboard/clients', screen: 'clients', guard: 'clients' },
    { key: 'ClientProfile', path: '/dashboard/clients/client', screen: ['clients', 'my-client'] },
    { key: 'Stores', path: '/dashboard/stores', screen: 'stores' },
    { key: 'StoreProfile', path: '/dashboard/stores/store', screen: 'stores' },
    { key: 'StoreCheck', path: '/dashboard/storeCheck', screen: 'stores' },
    { key: 'Users', path: '/dashboard/users', screen: 'users', guard: 'users' },
    { key: 'Permissions', path: '/dashboard/permissions', screen: 'permissions', guard: 'permissions' },
    { key: 'Prices', path: '/dashboard/price', screen: 'price', guard: 'price' },
    { key: 'Products', path: '/dashboard/products', screen: 'products', guard: 'products' },
    { key: 'PriceList', path: '/dashboard/price/list', screen: 'price', guard: 'price' },
    { key: 'Segments', path: '/dashboard/segments', screen: 'segments', guard: 'segments' },
    { key: 'Segment', path: '/dashboard/segments/segment', screen: 'segments', guard: 'segments' },
    { key: 'Promoters', path: '/dashboard/trade/promoters', screen: 'promoters' },
    { key: 'PromotersAnalytics', path: '/dashboard/trade/promoters/analytics', screen: 'promoters-analytics', guard: 'promoters-analytics' },
    { key: 'PromoterProfile', path: '/dashboard/trade/promoter', screen: 'promoters' },
    { key: 'Polls', path: '/dashboard/trade/polls', screen: 'polls', guard: 'polls' },
    { key: 'Auditors', path: '/dashboard/trade/auditors', screen: 'auditors', guard: 'auditors' },
    { key: 'AuditorFieldOptions', path: '/dashboard/trade/auditor-field-options', screen: 'auditor-field-options', guard: 'auditor-field-options' },
    { key: 'AuditConsole', path: '/dashboard/trade/audit-console', screen: 'audit-console', guard: 'audit-console' },
    { key: 'ProductApproval', path: '/dashboard/trade/product-approval', screen: 'product-approval', guard: 'product-approval' },
    { key: 'AuditorProfile', path: '/dashboard/trade/auditor', screen: 'auditors', guard: 'auditors' },
    { key: 'Contracts', path: '/dashboard/trade/contracts', screen: 'contracts', guard: 'contracts' },
    { key: 'Contract', path: '/dashboard/trade/contracts/contract', screen: 'contracts' },
    { key: 'Campaigns', path: '/dashboard/trade/campaigns', screen: 'campaigns', guard: 'campaigns' },
    { key: 'Campaign', path: '/dashboard/trade/campaigns/campaign', screen: 'campaigns' },
    { key: 'Tags', path: '/dashboard/tags', screen: 'tags', guard: 'tags' },
    { key: 'SOSAnalysis', path: '/dashboard/sos-analysis', screen: 'sos-analysis', guard: 'sos-analysis' },
    { key: 'ActivityLogs', path: '/dashboard/activity-logs', screen: 'activity-logs', guard: 'activity-logs' },
    { key: 'UserInteractions', path: '/dashboard/user-interactions', screen: 'user-interactions', guard: 'user-interactions' },
    { key: 'RouteEfficiency', path: '/dashboard/route-efficiency', screen: 'route-efficiency', guard: 'route-efficiency' },
    { key: 'DistributorPrices', path: '/dashboard/distributor-prices', screen: 'distributor-prices', guard: 'distributor-prices' },
    { key: 'LevantamientoGeneral', path: '/dashboard/levantamiento-general', screen: 'levantamiento-general', guard: 'levantamiento-general' },
    { key: 'LevantamientoCharts', path: '/dashboard/levantamiento-general/charts', screen: 'levantamiento-general', guard: 'levantamiento-general' },
    { key: 'UsoDeShelf', path: '/dashboard/reports/uso-de-shelf', screen: 'uso-de-shelf', guard: 'uso-de-shelf' },
    { key: 'ShareOfShelf', path: '/dashboard/reports/share-of-shelf', screen: 'share-of-shelf', guard: 'share-of-shelf' },
    { key: 'NumericDistribution', path: '/dashboard/reports/numeric-distribution', screen: 'numeric-distribution', guard: 'numeric-distribution' },
]);

export const BASE_ROUTE_KEYS = Object.freeze(BASE_ROUTES.map((route) => route.key));

/** Every nav key (screen) the base admin has. */
export const BASE_SCREENS = Object.freeze([
    ...new Set(BASE_ROUTES.flatMap((route) => (route.screen === null ? [] : [].concat(route.screen)))),
]);

/** Screens of a route as an array (empty for public routes). */
export function routeScreens(route) {
    return route?.screen == null ? [] : [].concat(route.screen);
}

/**
 * Base route keys a company drops for a screen allowlist: every non-public route none of
 * whose screens is allowed.
 * @param {readonly string[]} allowedScreens
 * @returns {string[]}
 */
export function routesOutside(allowedScreens) {
    return BASE_ROUTES.filter((route) => route.screen !== null && !routeScreens(route).some((s) => allowedScreens.includes(s))).map(
        (route) => route.key
    );
}
